import type { QualMancheRecap } from '@multirx/shared/types/championship.js';
import { DIVISIONS } from '@multirx/shared/types/division.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { createPillButton } from '../ui/pillButton.js';

let activeOverlay: HTMLElement | null = null;

function divisionLabel(division: QualMancheRecap['division']): string {
  return DIVISIONS.find((d) => d.id === division)?.shortLabel ?? `D${division}`;
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function buildRecapTableRows(recap: QualMancheRecap): string {
  return recap.rows
    .map((row, index) => {
      const bonusClass = row.championshipBonus > 0 ? ' championship-qual-recap-row--bonus' : '';
      const bonusCell =
        row.championshipBonus > 0 ? `<span class="championship-qual-recap-bonus">✓</span>` : '—';
      return `<tr class="championship-qual-recap-row${bonusClass}">
        <td class="championship-qual-recap-cell championship-qual-recap-cell--rank">${index + 1}</td>
        <td class="championship-qual-recap-cell championship-qual-recap-cell--driver">${escapeHtml(labelOwnDriver(row.driverName, row.driverId))}</td>
        <td class="championship-qual-recap-cell championship-qual-recap-cell--pts">${row.qualPoints}</td>
        <td class="championship-qual-recap-cell championship-qual-recap-cell--dice">${row.diceRolls}</td>
        <td class="championship-qual-recap-cell championship-qual-recap-cell--bonus">${bonusCell}</td>
      </tr>`;
    })
    .join('');
}

function bonusSubtitle(recap: QualMancheRecap): string {
  const winners = recap.rows.filter((r) => r.championshipBonus > 0);
  if (winners.length === 0) {
    return `Minimum de lancers : ${recap.fewestDiceRolls}`;
  }
  const names = winners.map((w) => labelOwnDriver(w.driverName, w.driverId)).join(', ');
  return `Moins de lancers cette manche (${recap.fewestDiceRolls}) : ${names}`;
}

export function showChampionshipQualMancheRecap(
  recap: QualMancheRecap,
  onClose?: () => void
): void {
  activeOverlay?.remove();
  activeOverlay = null;

  const overlay = document.createElement('div');
  overlay.className = 'championship-session-results-overlay championship-qual-recap-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Récapitulatif manche qualification');

  const card = document.createElement('div');
  card.className =
    'play-race-results-card championship-session-results-card championship-qual-recap-card';

  const title = document.createElement('h2');
  title.className = 'play-race-results-title';
  title.textContent = `Fin manche ${recap.manche} — qualification`;

  const subtitle = document.createElement('p');
  subtitle.className = 'play-race-results-subtitle championship-qual-recap-subtitle';
  subtitle.textContent = `${divisionLabel(recap.division)} · ${bonusSubtitle(recap)}`;

  const tableWrap = document.createElement('div');
  tableWrap.className = 'play-race-results-table-wrap';
  const table = document.createElement('table');
  table.className = 'play-race-results-table championship-qual-recap-table';
  table.innerHTML = `<thead>
    <tr>
      <th scope="col">#</th>
      <th scope="col">Pilote</th>
      <th scope="col" title="Points qualification">Pts qualif</th>
      <th scope="col" title="Lancers de dé">🎲</th>
      <th scope="col" title="Moins de lancers cette manche">Min.</th>
    </tr>
  </thead>`;
  const tbody = document.createElement('tbody');
  tbody.innerHTML = buildRecapTableRows(recap);
  table.appendChild(tbody);
  tableWrap.appendChild(table);

  const hint = document.createElement('p');
  hint.className = 'championship-qual-recap-hint';
  hint.textContent =
    'Les points de qualification classent le meeting uniquement. Le championnat ne compte que les places en demi-finale et finale. En fin de qualifications, +3 points de course pour le moins de lancers cumulés.';

  const actions = document.createElement('div');
  actions.className = 'play-race-results-actions';
  const closeBtn = createPillButton('Continuer', { variant: 'cta', large: true });
  const dismiss = (): void => {
    overlay.remove();
    if (activeOverlay === overlay) activeOverlay = null;
    if (onClose) {
      onClose();
      return;
    }
    window.location.hash = 'saisons';
  };
  if (closeBtn instanceof HTMLButtonElement) {
    closeBtn.type = 'button';
    closeBtn.onclick = dismiss;
  }
  actions.appendChild(closeBtn);

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) dismiss();
  });

  card.append(title, subtitle, tableWrap, hint, actions);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
  activeOverlay = overlay;
}

export function dismissChampionshipQualMancheRecap(): void {
  activeOverlay?.remove();
  activeOverlay = null;
}
